import React, { useCallback, useRef, useState } from "react"
import { Copy, Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { formatDate } from "../../utils/date/common"
import { fmtMoney, paxText } from "../../utils/grid/common"
import FloatingPanel from "../FloatingPanel"

const PriceCell = ({
  prices = [],
  typeId,
  baseId,
  buildNewUrl,
  buildEditUrl,
  onDelete,
  busyIds,
  transferTypeLabels,
}) => {
  const { t } = useTranslation()
  const btnRef = useRef(null)
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const typeParam = typeId || undefined

  if (!prices.length) {
    return (
      <a
        href={buildNewUrl({ from_id: baseId, transport_type_id: typeParam })}
        title={t("admin.add_price_for_type", { defaultValue: "Добавить цену для этого типа" })}
        className="mx-auto flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-app-content-faint/40 text-app-content-faint hover:border-brand-main hover:text-brand-main hover:bg-brand-main/5 transition-colors"
      >
        <Plus className="w-4 h-4" />
      </a>
    )
  }

  const min = Math.min(...prices.map((p) => p.adult))
  const currency = prices[0].currency
  const many = prices.length > 1

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`mx-auto flex flex-col items-center rounded-input px-3 py-1.5 transition-colors hover:bg-brand-main/5 ${
          open ? "bg-brand-main/10" : ""
        }`}
      >
        <span className="font-semibold text-app-content leading-tight">
          {many && (
            <span className="mr-1 text-xs font-normal text-text-muted">
              {t("admin.from", { defaultValue: "от" })}
            </span>
          )}
          {fmtMoney(min)}
        </span>
        <span className="text-xs text-text-muted">
          {currency}
          {many && ` · ${prices.length}`}
        </span>
      </button>

      {open && (
        <FloatingPanel anchorEl={btnRef.current} onClose={close} width={320}>
          <ul className="max-h-[320px] overflow-y-auto divide-y divide-app-content-faint/10">
            {prices.map((p) => {
              const busy = busyIds.includes(p.id)
              return (
                <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-app-content">
                      {fmtMoney(p.adult)} {p.currency}
                    </p>
                    <p className="text-xs text-text-secondary truncate">
                      {[paxText(p), transferTypeLabels[p.transfer_type] ?? p.transfer_type]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {(p.from_date || p.to_date) && (
                      <p className="text-[10px] text-app-content-faint">
                        {formatDate(p.from_date)} – {formatDate(p.to_date)}
                      </p>
                    )}
                  </div>

                  <a
                    href={buildEditUrl(p.id)}
                    title={t("admin.edit", { defaultValue: "Редактировать" })}
                    className="p-1.5 text-text-secondary hover:text-brand-main"
                  >
                    <Pencil className="w-4 h-4" />
                  </a>
                  <a
                    href={buildNewUrl({ from_id: p.id, copy: 1, transport_type_id: typeParam })}
                    title={t("admin.copy", { defaultValue: "Копировать" })}
                    className="p-1.5 text-text-secondary hover:text-brand-main"
                  >
                    <Copy className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onDelete([p.id])}
                    title={t("admin.delete", { defaultValue: "Удалить" })}
                    className="p-1.5 text-red-500 hover:text-red-600 disabled:opacity-disabled"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>

          <a
            href={buildNewUrl({ from_id: baseId, transport_type_id: typeParam })}
            className="flex items-center justify-center gap-1.5 border-t border-app-content-faint/10 bg-bg-secondary/50 px-3 py-2.5 text-sm font-medium text-brand-main hover:bg-brand-main/5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t("admin.add_price", { defaultValue: "Добавить цену" })}
          </a>
        </FloatingPanel>
      )}
    </>
  )
}

export default PriceCell
